import { VALIDATION_RULES, ERROR_MESSAGES } from './constants';

export interface ValidationResult {
    isValid: boolean;
    error?: string;
}

// Card Number Validation
export const validateCardNumber = (value: string): ValidationResult => {
    if (!value.trim()) {
        return { isValid: false, error: ERROR_MESSAGES.REQUIRED };
    }

    const cleaned = value.replace(/\s/g, '');

    if (cleaned.length < VALIDATION_RULES.CARD_NUMBER.MIN_LENGTH ||
        cleaned.length > VALIDATION_RULES.CARD_NUMBER.MAX_LENGTH) {
        return { isValid: false, error: ERROR_MESSAGES.INVALID_CARD_NUMBER };
    }

    if (!VALIDATION_RULES.CARD_NUMBER.PATTERN.test(cleaned)) {
        return { isValid: false, error: ERROR_MESSAGES.INVALID_CARD_NUMBER };
    }

    return { isValid: true };
};

// Name Validation (for first name, last name, name on card)
export const validateName = (value: string): ValidationResult => {
    if (!value.trim()) {
        return { isValid: false, error: ERROR_MESSAGES.REQUIRED };
    }

    if (value.length < VALIDATION_RULES.NAME.MIN_LENGTH) {
        return { isValid: false, error: ERROR_MESSAGES.MIN_LENGTH(VALIDATION_RULES.NAME.MIN_LENGTH) };
    }

    if (value.length > VALIDATION_RULES.NAME.MAX_LENGTH) {
        return { isValid: false, error: ERROR_MESSAGES.MAX_LENGTH(VALIDATION_RULES.NAME.MAX_LENGTH) };
    }

    if (!VALIDATION_RULES.NAME.PATTERN.test(value)) {
        return { isValid: false, error: ERROR_MESSAGES.INVALID_NAME };
    }

    return { isValid: true };
};

// IBAN Validation
export const validateIBAN = (value: string): ValidationResult => {
    if (!value.trim()) {
        return { isValid: false, error: ERROR_MESSAGES.REQUIRED };
    }

    const cleaned = value.replace(/\s/g, '').toUpperCase();

    if (cleaned.length < VALIDATION_RULES.IBAN.MIN_LENGTH ||
        cleaned.length > VALIDATION_RULES.IBAN.MAX_LENGTH) {
        return { isValid: false, error: ERROR_MESSAGES.INVALID_IBAN };
    }

    if (!VALIDATION_RULES.IBAN.PATTERN.test(cleaned)) {
        return { isValid: false, error: ERROR_MESSAGES.INVALID_IBAN };
    }

    return { isValid: true };
};

// SWIFT/BIC Validation
export const validateSWIFT = (value: string): ValidationResult => {
    if (!value.trim()) {
        return { isValid: false, error: ERROR_MESSAGES.REQUIRED };
    }

    const cleaned = value.replace(/\s/g, '').toUpperCase();

    if (cleaned.length !== VALIDATION_RULES.SWIFT.MIN_LENGTH &&
        cleaned.length !== VALIDATION_RULES.SWIFT.MAX_LENGTH) {
        return { isValid: false, error: ERROR_MESSAGES.INVALID_SWIFT };
    }

    if (!VALIDATION_RULES.SWIFT.PATTERN.test(cleaned)) {
        return { isValid: false, error: ERROR_MESSAGES.INVALID_SWIFT };
    }

    return { isValid: true };
};

// Postcode Validation
export const validatePostcode = (value: string): ValidationResult => {
    if (!value.trim()) {
        return { isValid: false, error: ERROR_MESSAGES.REQUIRED };
    }

    if (value.length < VALIDATION_RULES.POSTCODE.MIN_LENGTH ||
        value.length > VALIDATION_RULES.POSTCODE.MAX_LENGTH) {
        return { isValid: false, error: ERROR_MESSAGES.INVALID_POSTCODE };
    }

    if (!VALIDATION_RULES.POSTCODE.PATTERN.test(value)) {
        return { isValid: false, error: ERROR_MESSAGES.INVALID_POSTCODE };
    }

    return { isValid: true };
};

// Amount Validation
export const validateAmount = (value: string): ValidationResult => {
    if (!value.trim()) {
        return { isValid: false, error: ERROR_MESSAGES.REQUIRED };
    }

    const numValue = parseFloat(value);

    if (isNaN(numValue)) {
        return { isValid: false, error: ERROR_MESSAGES.INVALID_AMOUNT };
    }

    if (numValue < VALIDATION_RULES.AMOUNT.MIN) {
        return { isValid: false, error: ERROR_MESSAGES.MIN_AMOUNT(VALIDATION_RULES.AMOUNT.MIN) };
    }

    if (numValue > VALIDATION_RULES.AMOUNT.MAX) {
        return { isValid: false, error: ERROR_MESSAGES.MAX_AMOUNT(VALIDATION_RULES.AMOUNT.MAX) };
    }

    if (!VALIDATION_RULES.AMOUNT.PATTERN.test(value)) {
        return { isValid: false, error: ERROR_MESSAGES.INVALID_AMOUNT };
    }

    return { isValid: true };
};

// Address Validation
export const validateAddress = (value: string): ValidationResult => {
    if (!value.trim()) {
        return { isValid: false, error: ERROR_MESSAGES.REQUIRED };
    }

    if (value.length < VALIDATION_RULES.ADDRESS.MIN_LENGTH ||
        value.length > VALIDATION_RULES.ADDRESS.MAX_LENGTH) {
        return { isValid: false, error: ERROR_MESSAGES.INVALID_ADDRESS };
    }

    return { isValid: true };
};

// City Validation
export const validateCity = (value: string): ValidationResult => {
    if (!value.trim()) {
        return { isValid: false, error: ERROR_MESSAGES.REQUIRED };
    }

    if (value.length < VALIDATION_RULES.CITY.MIN_LENGTH ||
        value.length > VALIDATION_RULES.CITY.MAX_LENGTH) {
        return { isValid: false, error: ERROR_MESSAGES.INVALID_CITY };
    }

    if (!VALIDATION_RULES.CITY.PATTERN.test(value)) {
        return { isValid: false, error: ERROR_MESSAGES.INVALID_CITY };
    }

    return { isValid: true };
};

// Business Name Validation
export const validateBusinessName = (value: string): ValidationResult => {
    if (!value.trim()) {
        return { isValid: false, error: ERROR_MESSAGES.REQUIRED };
    }

    if (value.length < VALIDATION_RULES.BUSINESS_NAME.MIN_LENGTH ||
        value.length > VALIDATION_RULES.BUSINESS_NAME.MAX_LENGTH) {
        return { isValid: false, error: ERROR_MESSAGES.INVALID_BUSINESS_NAME };
    }

    return { isValid: true };
};

// Required Field Validation
export const validateRequired = (value: string): ValidationResult => {
    if (!value.trim()) {
        return { isValid: false, error: ERROR_MESSAGES.REQUIRED };
    }
    return { isValid: true };
};

// Input Formatters
export const formatCardNumber = (value: string): string => {
    // Remove all non-digit characters
    const cleaned = value.replace(/\D/g, '');

    // Add space every 4 digits
    const formatted = cleaned.match(/.{1,4}/g)?.join(' ') || cleaned;

    return formatted.slice(0, 23); // Max 19 digits + 4 spaces
};

export const formatIBAN = (value: string): string => {
    // Remove all spaces and convert to uppercase
    const cleaned = value.replace(/\s/g, '').toUpperCase();

    // Add space every 4 characters
    const formatted = cleaned.match(/.{1,4}/g)?.join(' ') || cleaned;

    return formatted.slice(0, 42); // Max 34 chars + 8 spaces
};

export const formatSWIFT = (value: string): string => {
    return value.replace(/\s/g, '').toUpperCase().slice(0, 11);
};

export const formatAmount = (value: string): string => {
    // Allow only numbers and one decimal point
    const cleaned = value.replace(/[^\d.]/g, '');

    // Ensure only one decimal point
    const parts = cleaned.split('.');
    if (parts.length > 2) {
        return parts[0] + '.' + parts.slice(1).join('');
    }

    // Limit to 2 decimal places
    if (parts[1]?.length > 2) {
        return parts[0] + '.' + parts[1].slice(0, 2);
    }

    return cleaned;
};